import { useCallback, useEffect, useRef, useState } from 'react';
import {
	appendPersistedChart,
	buildCompositionChartSnapshot,
	buildOverdueChartSnapshot,
	buildPipelineChartSnapshot,
	buildPersistedChartSnapshot,
	clearPersistedChartsLocal,
	cyclePersistedChartColSpan,
	cyclePersistedChartRowSpan,
	duplicatePersistedChart,
	hasReachedPersistedChartsLimit,
	isPipelineBuiltinChartKind,
	loadPersistedCharts,
	removePersistedChart,
	replacePersistedChart,
	renamePersistedChart,
	reorderPersistedCharts,
	resolveDefaultHomeDashboardChartColSpan,
	resolvePersistedChartKind,
	PERSISTED_CHART_COL_SPAN_MAX,
	PERSISTED_CHART_KIND,
	DASHBOARD_CHART_BOARD,
	updatePersistedChartColSpan,
	updatePersistedChartRowSpan,
} from '../lib/statisticsPersistedCharts';
import { attachFrozenDataSnapshot, attachLiveRefreshMode, isFrozenChartSnapshot, isLiveChartSnapshot } from '../lib/statisticsChartRefreshMode';
import { serializeChartBundleForSnapshot } from '../lib/chartDataSnapshot';
import { buildFrozenFromLive, buildLiveFromFrozen, resolveChartRefreshMode } from '../lib/dashboardChartLifecycle';

/** 制作区与图表库各有一份 hook 实例；删除中的 id 需跨实例共享，避免旧缓存把图写回。 */
const pendingDeletedChartIdsByBoard = new Map();

const getPendingDeletedChartIds = (board) => {
	if (!pendingDeletedChartIdsByBoard.has(board)) {
		pendingDeletedChartIdsByBoard.set(board, new Set());
	}
	return pendingDeletedChartIdsByBoard.get(board);
};

const omitPendingDeletedCharts = (charts, board) => {
	const pending = getPendingDeletedChartIds(board);
	if (!pending.size || !Array.isArray(charts)) return charts;
	return charts.filter((chart) => !pending.has(chart?.id));
};

const buildSnapshotForSave = ({
	filters,
	selectedPhase,
	title,
	chartKind,
	chartConfig,
	overdueConfig,
	chartBundle,
	refreshMode,
	sourceStatisticsChartId,
	board,
	liveDisplayFallback,
	homeChartCountAfterAdd,
	existingMeta = null,
	pinMeta = null,
	usesPageFilters = false,
}) => {
	const resolvedKind = resolvePersistedChartKind({ chartKind });
	let snapshot;
	if (resolvedKind === PERSISTED_CHART_KIND.OVERDUE) {
		snapshot = buildOverdueChartSnapshot({ title, overdueConfig });
	} else if (resolvedKind === PERSISTED_CHART_KIND.COMPOSITION) {
		snapshot = buildCompositionChartSnapshot({ filters, selectedPhase, title, chartConfig });
	} else if (isPipelineBuiltinChartKind(resolvedKind)) {
		snapshot = buildPipelineChartSnapshot({ filters, selectedPhase, title, chartKind: resolvedKind });
	} else {
		snapshot = buildPersistedChartSnapshot({ filters, selectedPhase, title });
	}

	const meta = pinMeta ?? existingMeta;
	if (meta) {
		snapshot = {
			...snapshot,
			...(meta.source ? { source: meta.source } : {}),
			...(meta.templateId ? { templateId: meta.templateId } : {}),
		};
	}

	if (refreshMode === 'frozen') {
		return { ...attachFrozenDataSnapshot(snapshot, chartBundle), board, usesPageFilters: false };
	}
	const liveSnapshot = attachLiveRefreshMode(snapshot);
	const defaultHomeColSpan = resolveDefaultHomeDashboardChartColSpan(homeChartCountAfterAdd ?? 1);
	const pageBound = board !== DASHBOARD_CHART_BOARD.HOME && Boolean(usesPageFilters);
	return {
		...liveSnapshot,
		board,
		usesPageFilters: pageBound,
		...(sourceStatisticsChartId ? { sourceStatisticsChartId } : {}),
		...(liveDisplayFallback && typeof liveDisplayFallback === 'object' ? { liveDisplayFallback } : {}),
		ui: {
			...(liveSnapshot.ui || {}),
			colSpan: liveSnapshot.ui?.colSpan ?? defaultHomeColSpan,
		},
	};
};

export const useDashboardChartsPersistence = ({ userId, enabled = true, refreshMode = 'frozen', maxCharts, storagePrefix, board, useGetChartsQuery, usePutChartsMutation }) => {
	const skip = !userId || !enabled;
	const { data, isLoading, isFetching, isSuccess, isError } = useGetChartsQuery(undefined, {
		skip,
		refetchOnFocus: false,
		refetchOnReconnect: false,
	});
	const [putCharts, { isLoading: isSaving }] = usePutChartsMutation();

	const [charts, setCharts] = useState(() => {
		if (skip || !userId) return [];
		return loadPersistedCharts(userId, storagePrefix);
	});
	const [isChartsReady, setIsChartsReady] = useState(() => {
		if (skip) return true;
		if (!userId) return false;
		return loadPersistedCharts(userId, storagePrefix).length > 0;
	});
	const migrationDoneRef = useRef(false);
	const chartsRef = useRef(charts);
	const persistQueueRef = useRef(Promise.resolve());
	const persistEpochRef = useRef(0);
	const persistInFlightRef = useRef(0);
	chartsRef.current = charts;

	const persistCharts = useCallback(
		(nextCharts, { applyServerResponse = true } = {}) => {
			if (!userId || !enabled) return Promise.resolve(null);

			const epoch = ++persistEpochRef.current;
			const previous = chartsRef.current;
			chartsRef.current = nextCharts;
			setCharts(nextCharts);
			persistInFlightRef.current += 1;

			const run = async () => {
				try {
					const response = await putCharts(nextCharts).unwrap();
					const saved = omitPendingDeletedCharts(response?.charts ?? nextCharts, board);
					if (applyServerResponse && epoch === persistEpochRef.current) {
						chartsRef.current = saved;
						setCharts(saved);
					}
					return saved;
				} catch {
					if (epoch === persistEpochRef.current) {
						chartsRef.current = previous;
						setCharts(previous);
					}
					return null;
				} finally {
					persistInFlightRef.current = Math.max(0, persistInFlightRef.current - 1);
				}
			};

			const queued = persistQueueRef.current.then(run);
			persistQueueRef.current = queued.catch(() => {});
			return queued;
		},
		[board, enabled, putCharts, userId],
	);

	const persistChartsLayout = useCallback((nextCharts) => persistCharts(nextCharts, { applyServerResponse: false }), [persistCharts]);

	useEffect(() => {
		migrationDoneRef.current = false;
		if (skip) {
			setCharts([]);
			setIsChartsReady(true);
			return;
		}
		// 用户切换挂载：若 RTK 已有该用户缓存（含首页预取），立刻展示，避免整块空等
		if (Array.isArray(data?.charts)) {
			setCharts(data.charts);
			setIsChartsReady(true);
			return;
		}
		const localCharts = loadPersistedCharts(userId, storagePrefix);
		if (localCharts.length > 0) {
			setCharts(localCharts);
			setIsChartsReady(true);
			return;
		}
		setCharts([]);
		setIsChartsReady(false);
		// 仅随 identity 重置；data 到达由下方 isSuccess effect 处理
		// eslint-disable-next-line react-hooks/exhaustive-deps -- 故意不把 data?.charts 列入 deps，避免 refetch 反复重置 migration
	}, [skip, storagePrefix, userId]);

	useEffect(() => {
		if (skip || isChartsReady) return;
		if (isError) {
			setCharts([]);
			setIsChartsReady(true);
		}
	}, [isChartsReady, isError, skip]);

	useEffect(() => {
		if (!isSuccess || skip || migrationDoneRef.current) return;

		const serverCharts = data?.charts ?? [];
		const localCharts = loadPersistedCharts(userId, storagePrefix);

		if (serverCharts.length === 0 && localCharts.length > 0) {
			migrationDoneRef.current = true;
			putCharts(localCharts)
				.unwrap()
				.then((response) => {
					setCharts(response?.charts ?? localCharts);
					clearPersistedChartsLocal(userId, storagePrefix);
				})
				.catch(() => {
					setCharts(serverCharts);
				})
				.finally(() => {
					setIsChartsReady(true);
				});
			return;
		}

		migrationDoneRef.current = true;
		setCharts(serverCharts);
		setIsChartsReady(true);
	}, [data?.charts, isSuccess, putCharts, skip, storagePrefix, userId]);

	/**
	 * 同页可有多份 hook 实例（制作区保存 vs 图表库展示），本地 useState 互不共享。
	 * PUT 会更新 RTK 缓存；此处把已就绪实例的本地列表跟缓存对齐，避免固定成功但图表库不刷新。
	 * 删除进行中时忽略仍含该图的旧缓存，避免刚删掉的图被写回。
	 */
	useEffect(() => {
		if (skip || !isChartsReady || !Array.isArray(data?.charts)) return;
		if (persistInFlightRef.current > 0) return;
		const next = omitPendingDeletedCharts(data.charts, board);
		chartsRef.current = next;
		setCharts(next);
	}, [board, data?.charts, isChartsReady, skip]);

	const saveChart = useCallback(
		async ({
			filters,
			selectedPhase,
			title,
			chartKind = PERSISTED_CHART_KIND.TREND,
			chartConfig,
			overdueConfig,
			chartBundle,
			sourceStatisticsChartId,
			liveDisplayFallback,
			pinMeta,
			refreshMode: refreshModeOverride,
			showPointLabels,
			usesPageFilters = false,
		}) => {
			const resolvedRefreshMode = refreshModeOverride ?? refreshMode;
			const nextCount = chartsRef.current.length + 1;
			let snapshot = buildSnapshotForSave({
				filters,
				selectedPhase,
				title,
				chartKind,
				chartConfig,
				overdueConfig,
				chartBundle,
				refreshMode: resolvedRefreshMode,
				sourceStatisticsChartId,
				board,
				liveDisplayFallback,
				homeChartCountAfterAdd: resolvedRefreshMode === 'live' ? nextCount : undefined,
				pinMeta,
				usesPageFilters,
			});

			if (typeof showPointLabels === 'boolean') {
				snapshot = {
					...snapshot,
					ui: {
						...(snapshot.ui || {}),
						showPointLabels,
					},
				};
			}

			if (resolvedRefreshMode === 'frozen' && !snapshot.dataSnapshot) {
				return { charts: chartsRef.current, added: false, reason: 'missing-data' };
			}

			let baseCharts = chartsRef.current;
			if (resolvedRefreshMode === 'live' && nextCount > 1) {
				const sharedColSpan = resolveDefaultHomeDashboardChartColSpan(nextCount);
				baseCharts = baseCharts.map((chart) => {
					const saved = chart.ui?.colSpan;
					const isDefaultFullWidth = !Number.isFinite(saved) || saved === PERSISTED_CHART_COL_SPAN_MAX;
					if (!isDefaultFullWidth) return chart;
					return {
						...chart,
						ui: {
							...(chart.ui || {}),
							colSpan: sharedColSpan,
						},
					};
				});
			}

			const result = appendPersistedChart(baseCharts, snapshot, maxCharts, {
				dedupe: true,
			});
			if (!result.added) {
				return result;
			}

			const saved = await persistCharts(result.charts);
			if (!saved) {
				return { charts: chartsRef.current, added: false, reason: 'persist-failed' };
			}

			return { ...result, charts: saved };
		},
		[maxCharts, persistCharts, refreshMode, board],
	);

	const updateChart = useCallback(
		async ({
			chartId,
			filters,
			selectedPhase,
			title,
			chartKind,
			chartConfig,
			overdueConfig,
			chartBundle,
			showPointLabels,
			refreshMode: refreshModeOverride,
			usesPageFilters: usesPageFiltersOverride,
		}) => {
			const existing = chartsRef.current.find((item) => item.id === chartId);
			if (!existing) {
				return { charts: chartsRef.current, updated: false, reason: 'missing' };
			}

			const resolvedKind = resolvePersistedChartKind({ chartKind: chartKind ?? existing.chartKind });
			const chartRefreshMode =
				refreshModeOverride === 'frozen' || refreshModeOverride === 'live'
					? refreshModeOverride
					: resolveChartRefreshMode(existing, refreshMode);

			const snapshot = buildSnapshotForSave({
				filters,
				selectedPhase,
				title: title?.trim() || existing.title,
				chartKind: resolvedKind,
				chartConfig,
				overdueConfig,
				chartBundle,
				refreshMode: chartRefreshMode,
				sourceStatisticsChartId: existing.sourceStatisticsChartId,
				board,
				existingMeta: {
					source: existing.source,
					templateId: existing.templateId,
				},
				usesPageFilters:
					usesPageFiltersOverride === undefined ? Boolean(existing.usesPageFilters) : Boolean(usesPageFiltersOverride),
			});

			if (chartRefreshMode === 'frozen' && !snapshot.dataSnapshot) {
				return { charts: chartsRef.current, updated: false, reason: 'missing-data' };
			}

			const nextUi = { ...(existing.ui ?? {}) };
			if (typeof showPointLabels === 'boolean') {
				nextUi.showPointLabels = showPointLabels;
			}

			const nextSnapshot = {
				...snapshot,
				id: chartId,
				createdAt: existing.createdAt,
				title: title?.trim() || existing.title || snapshot.title,
				ui: nextUi,
				board: existing.board ?? board,
				...(existing.sourceStatisticsChartId ? { sourceStatisticsChartId: existing.sourceStatisticsChartId } : {}),
				...(existing.source ? { source: existing.source } : {}),
				...(existing.templateId ? { templateId: existing.templateId } : {}),
			};

			if (chartRefreshMode === 'live') {
				delete nextSnapshot.dataSnapshot;
				delete nextSnapshot.pinnedAt;
			} else {
				nextSnapshot.pinnedAt = new Date().toISOString();
				nextSnapshot.usesPageFilters = false;
			}

			const result = replacePersistedChart(chartsRef.current, chartId, nextSnapshot);
			if (!result.updated) {
				return { charts: chartsRef.current, updated: false, reason: result.reason };
			}

			const saved = await persistCharts(result.charts);
			if (!saved) {
				return { charts: chartsRef.current, updated: false, reason: 'persist-failed' };
			}

			return { ...result, charts: saved };
		},
		[board, persistCharts, refreshMode],
	);

	const refreshChartSnapshot = useCallback(
		(chartId, chartBundle) => {
			const chart = chartsRef.current.find((item) => item.id === chartId);
			if (!chart || !isFrozenChartSnapshot(chart)) {
				return { updated: false, reason: 'not-frozen' };
			}
			const dataSnapshot = serializeChartBundleForSnapshot(chartBundle, chart.chartKind);
			if (!dataSnapshot) {
				return { updated: false, reason: 'missing-bundle' };
			}
			const next = chartsRef.current.map((item) =>
				item.id === chartId
					? {
							...item,
							dataSnapshot,
							pinnedAt: new Date().toISOString(),
							refreshMode: 'frozen',
							usesPageFilters: false,
						}
					: item,
			);
			persistCharts(next);
			return { updated: true };
		},
		[persistCharts],
	);

	const freezeChart = useCallback(
		async (chartId, { chartBundle = null, appliedFilters = null } = {}) => {
			const chart = chartsRef.current.find((item) => item.id === chartId);
			if (!chart) {
				return { updated: false, reason: 'missing' };
			}
			if (isFrozenChartSnapshot(chart) && chart.dataSnapshot) {
				return { updated: false, reason: 'already-frozen', charts: chartsRef.current };
			}
			const frozen = buildFrozenFromLive({ snapshot: chart, appliedFilters, chartBundle });
			if (!frozen) {
				return { updated: false, reason: 'invalid' };
			}
			if (chart.chartKind !== PERSISTED_CHART_KIND.OVERDUE && !frozen.dataSnapshot) {
				return { updated: false, reason: 'missing-data' };
			}
			const result = replacePersistedChart(chartsRef.current, chartId, { ...frozen, id: chartId });
			if (!result.updated) {
				return { charts: chartsRef.current, updated: false, reason: result.reason };
			}
			const saved = await persistCharts(result.charts);
			if (!saved) {
				return { charts: chartsRef.current, updated: false, reason: 'persist-failed' };
			}
			return { ...result, charts: saved };
		},
		[persistCharts],
	);

	const thawChart = useCallback(
		async (chartId) => {
			const chart = chartsRef.current.find((item) => item.id === chartId);
			if (!chart) {
				return { updated: false, reason: 'missing' };
			}
			if (isLiveChartSnapshot(chart) && !chart.dataSnapshot) {
				return { updated: false, reason: 'already-live', charts: chartsRef.current };
			}
			const live = buildLiveFromFrozen(chart);
			const result = replacePersistedChart(chartsRef.current, chartId, { ...live, id: chartId });
			if (!result.updated) {
				return { charts: chartsRef.current, updated: false, reason: result.reason };
			}
			const saved = await persistCharts(result.charts);
			if (!saved) {
				return { charts: chartsRef.current, updated: false, reason: 'persist-failed' };
			}
			return { ...result, charts: saved };
		},
		[persistCharts],
	);

	const deleteChart = useCallback(
		(chartId) => {
			const previous = chartsRef.current;
			const next = removePersistedChart(previous, chartId);
			if (next.length === previous.length) return;

			const pending = getPendingDeletedChartIds(board);
			pending.add(chartId);
			chartsRef.current = next;
			setCharts(next);

			const epochBefore = persistEpochRef.current;
			persistCharts(next).then((saved) => {
				if (saved != null) return;
				pending.delete(chartId);
				if (persistEpochRef.current === epochBefore + 1) {
					chartsRef.current = previous;
					setCharts(previous);
				}
			});
		},
		[board, persistCharts],
	);

	const reorderCharts = useCallback(
		(sourceId, targetId) => {
			const next = reorderPersistedCharts(chartsRef.current, sourceId, targetId);
			persistChartsLayout(next);
		},
		[persistChartsLayout],
	);

	const renameChart = useCallback(
		(chartId, title) => {
			const result = renamePersistedChart(chartsRef.current, chartId, title);
			if (result.renamed) {
				persistChartsLayout(result.charts);
			}
			return result;
		},
		[persistChartsLayout],
	);

	const duplicateChart = useCallback(
		(chartId, { appliedFilters = null } = {}) => {
			const result = duplicatePersistedChart(chartsRef.current, chartId, maxCharts, { appliedFilters });
			if (result.added) {
				persistCharts(result.charts);
			}
			return result;
		},
		[maxCharts, persistCharts],
	);

	const resizeChartColSpan = useCallback(
		(chartId, colSpan, maxColumns) => {
			const result = updatePersistedChartColSpan(chartsRef.current, chartId, colSpan, maxColumns);
			if (result.updated) {
				persistChartsLayout(result.charts);
			}
			return result;
		},
		[persistChartsLayout],
	);

	const resizeChartRowSpan = useCallback(
		(chartId, rowSpan) => {
			const result = updatePersistedChartRowSpan(chartsRef.current, chartId, rowSpan);
			if (result.updated) {
				persistChartsLayout(result.charts);
			}
			return result;
		},
		[persistChartsLayout],
	);

	const cycleChartColSpan = useCallback(
		(chartId, maxColumns) => {
			const result = cyclePersistedChartColSpan(chartsRef.current, chartId, maxColumns);
			if (result.updated) {
				persistChartsLayout(result.charts);
			}
			return result;
		},
		[persistChartsLayout],
	);

	const cycleChartRowSpan = useCallback(
		(chartId) => {
			const result = cyclePersistedChartRowSpan(chartsRef.current, chartId);
			if (result.updated) {
				persistChartsLayout(result.charts);
			}
			return result;
		},
		[persistChartsLayout],
	);

	const applyServerCharts = useCallback((nextCharts) => {
		if (!Array.isArray(nextCharts)) return;
		const pending = getPendingDeletedChartIds(board);
		nextCharts.forEach((chart) => {
			if (chart?.id) pending.delete(chart.id);
		});
		persistEpochRef.current += 1;
		chartsRef.current = nextCharts;
		setCharts(nextCharts);
	}, [board]);

	const resolvedCharts = omitPendingDeletedCharts(isChartsReady ? charts : (data?.charts ?? charts), board);
	const savedChartsCount = resolvedCharts.length;
	const canAddMore = !hasReachedPersistedChartsLimit(savedChartsCount, maxCharts);

	return {
		charts: resolvedCharts,
		saveChart,
		updateChart,
		refreshChartSnapshot,
		freezeChart,
		thawChart,
		deleteChart,
		reorderCharts,
		renameChart,
		duplicateChart,
		resizeChartColSpan,
		cycleChartColSpan,
		resizeChartRowSpan,
		cycleChartRowSpan,
		applyServerCharts,
		savedChartsCount,
		canAddMore,
		maxCharts,
		isLoading: isLoading || isFetching,
		isChartsReady,
		isChartsLoading: !skip && !isChartsReady && resolvedCharts.length === 0,
		isSaving,
		refreshMode,
	};
};

export default useDashboardChartsPersistence;
